import {
  DatePipe,
  DecimalPipe,
  registerLocaleData,
  TitleCasePipe,
} from '@angular/common';
import {
  HttpClient,
  HttpClientModule,
  HTTP_INTERCEPTORS,
} from '@angular/common/http';
import localePt from '@angular/common/locales/pt';
import { forwardRef, LOCALE_ID, NgModule } from '@angular/core';
import { AngularFireModule } from '@angular/fire/compat';
import { AngularFirestoreModule } from '@angular/fire/compat/firestore';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AuthInterceptor } from '@app/services';
import { SharedModule } from '@app/shared';
import { AiAssistantModule } from '@evollo/ai-assistant';
import { NgbDateParserFormatter } from '@ng-bootstrap/ng-bootstrap';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { CollapseModule } from 'ngx-bootstrap/collapse';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { TooltipConfig, TooltipModule } from 'ngx-bootstrap/tooltip';
import { MarkdownModule } from 'ngx-markdown';
import { IConfig, NgxMaskModule } from 'ngx-mask';
import { NgxPermissionsModule } from 'ngx-permissions';
import { NgxPowerBiModule } from 'ngx-powerbi';
import { getPtBrPaginatorIntl } from '../assets/i18n/pt-br-paginator-intl';
import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { EvoSwitchComponent } from './_components/evo-switch/evo-switch.component';
import { FixedTopHeaderComponent } from './_components/header/fixed-top-header.component';
import { NotificationBellComponent } from './_components/notification-bell/notification-bell.component';
import { SearchInputComponent } from './_components/search-input/search-input.component';
import { ChangeNotesModalComponent } from './_components/change-notes-modal/change-notes-modal.component';
import { EvoNavigationBarComponent } from './_components/sidenav/evo-navigation-bar.component';
import { WebsocketComponent } from './_components/sidenav/websocket/websocket.component';
import { ErrorInterceptor } from './_helpers/error.interceptor';
import { NgbDateParserHelper } from './_helpers/ngb-date-parser.helper';
import { CheckIFDate } from './_helpers/pipes/checkIFDate.pipe';
import { CheckIFDateHour } from './_helpers/pipes/checkIFDateHour.pipe';
import { CleanNumberPipe } from './_helpers/pipes/clean-number.pipe';
import { TruncatePipe } from './_helpers/pipes/truncate.pipe';
import { SidebarMenuComponent } from './_helpers/sidebar-menu/sidebar-menu.component';
import { CacheInterceptorService } from './_services/cache-interceptor.service';
import { LoginComponent } from './_views/login/login.component';
import { NoAccessComponent } from './_views/no-access/no-access.component';
import { OrganizationsComponent } from './_views/organizations/organizations.component';

export const options: Partial<IConfig> | (() => Partial<IConfig>) = null;

export function HttpLoaderFactory(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http);
}

export function getTooltipConfig(): TooltipConfig {
  return Object.assign(new TooltipConfig(), {
    container: 'body',
  });
}

@NgModule({
  declarations: [
    AppComponent,
    FixedTopHeaderComponent,
    NotificationBellComponent,
    EvoNavigationBarComponent,
    EvoSwitchComponent,
    SidebarMenuComponent,
    LoginComponent,
    OrganizationsComponent,
    NoAccessComponent,
    WebsocketComponent,
    ChangeNotesModalComponent,
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    HttpClientModule,
    BrowserAnimationsModule,
    SharedModule,
    NgxMaskModule.forRoot(),
    NgxPermissionsModule.forRoot(),
    BsDropdownModule.forRoot(),
    CollapseModule.forRoot(),
    TooltipModule.forRoot(),
    NgxPowerBiModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    AiAssistantModule,
    MarkdownModule.forRoot(),
    AngularFireModule.initializeApp(environment.firebaseConfig),
    AngularFirestoreModule,
  ],
  providers: [
    TruncatePipe,
    CleanNumberPipe,
    CheckIFDate,
    CheckIFDateHour,
    BsModalService,
    BsModalRef,
    DatePipe,
    DecimalPipe,
    TitleCasePipe,
    { provide: NgbDateParserFormatter, useClass: NgbDateParserHelper },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CacheInterceptorService,
      multi: true,
    },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SearchInputComponent),
      multi: true,
    },
    { provide: LOCALE_ID, useValue: 'pt-BR' },
    { provide: MatPaginatorIntl, useValue: getPtBrPaginatorIntl() },
    { provide: TooltipConfig, useFactory: getTooltipConfig },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor(private titleCasePipe: TitleCasePipe) {
    const capitalize = (months: string[], month: string): string[] => {
      return [...months, titleCasePipe.transform(month)] as string[];
    };

    localePt[5][1] = localePt[5][2].reduce(capitalize, []);
    localePt[5][2] = localePt[5][2].reduce(capitalize, []);

    registerLocaleData(localePt);
  }
}
